/**
 * Dashboard.tsx — Top-level dashboard component
 * Manages page routing and renders the correct page inside DashboardLayout
 */

import { useState, useEffect } from 'react'
import { DashboardLayout } from './DashboardLayout'
import { OverviewHome } from './pages/OverviewHome'
import { SystemHealth } from './pages/SystemHealth'
import { AgentsList } from './pages/AgentsList'
import { ActiveRuns } from './pages/ActiveRuns'
import { ScheduledJobs } from './pages/ScheduledJobs'
import { RunPerformance } from './pages/RunPerformance'
import { ModelUsage } from './pages/ModelUsage'
import { FeedbackData } from './pages/FeedbackData'
import { LinkedInScraping } from './pages/LinkedInScraping'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { ComingSoon } from './DashboardShared'
import { OperationsSops } from './pages/operations/OperationsSops'
import { OperationsQuality } from './pages/operations/OperationsQuality'
import { OperationsIncidents } from './pages/operations/OperationsIncidents'
import { OperationsAssets } from './pages/operations/OperationsAssets'
import { OperationsTasks } from './pages/operations/OperationsTasks'
import { OperationsOverview } from './pages/operations/OperationsOverview'
import { OperationsFieldTeam } from './pages/operations/OperationsFieldTeam'
import { Plus } from 'lucide-react'
import { SalesDashboard } from './pages/sales/SalesDashboard'
import { SalesPipeline } from './pages/sales/SalesPipeline'
import { SalesContacts } from './pages/sales/SalesContacts'
import { SalesReports } from './pages/sales/SalesReports'
import { SalesActivities } from './pages/sales/SalesActivities'
import { SalesDealDetail } from './pages/sales/SalesDealDetail'
import { SalesArchive } from './pages/sales/SalesArchive'
import { SalesLeads } from './pages/sales/SalesLeads'
import { SalesLeadDetail } from './pages/sales/SalesLeadDetail'
import { SalesAccounts } from './pages/sales/SalesAccounts'
import { SalesAccountDetail } from './pages/sales/SalesAccountDetail'
import HROverview from './pages/hr/HROverview'
import HREmployees from './pages/hr/HREmployees'
import HRLifecycle from './pages/hr/HRLifecycle'
import HRCandidates from './pages/hr/HRCandidates'
import HROnboarding from './pages/hr/HROnboarding'
import HRJobPostings from './pages/hr/HRJobPostings'
import HRProviders from './pages/hr/HRProviders'
import HRTickets from './pages/hr/HRTickets'
import HROrgChart from './pages/hr/HROrgChart'
import TriageQueue from './pages/inbox/TriageQueue'
import PayrollPayRun from './pages/payroll/PayrollPayRun'
import FinanceOverview from './pages/finance/FinanceOverview'
import FinanceInvoices from './pages/finance/FinanceInvoices'
import FinanceApprovals from './pages/finance/FinanceApprovals'
import FinanceRejections from './pages/finance/FinanceRejections'
import FinanceExpenses from './pages/finance/FinanceExpenses'
import FinanceReports from './pages/finance/FinanceReports'
import IssuedInvoices from './pages/finance/IssuedInvoices'
import SettingsPage from './pages/settings/SettingsPage'
import { type ApiHRJobPosting, type MeResponse } from './types'
import { fetchMe } from './api'

const PLACEHOLDER_PAGES: Record<string, { title: string; description: string }> = {
  'automation.workflows': { title: 'Workflows', description: 'Visual workflow builder for multi-agent pipelines' },

  'documents.recent': { title: 'Recent Runs', description: 'Document processing run history' },
  'documents.cv': { title: 'CV Screening', description: 'AI-scored candidate shortlists' },
  'documents.invoices': { title: 'Invoice Processing', description: 'Automated invoice extraction and approval' },
  'documents.contracts': { title: 'Contracts', description: 'Contract review and clause analysis' },
  'operations.hr': { title: 'HR Queries', description: 'Employee query automation' },
  'operations.jobs': { title: 'Job Postings', description: 'Multi-board job posting automation' },
  'operations.email': { title: 'Email Automation', description: 'Outreach sequences and reply handling' },
  'scraping.web': { title: 'Web Scraper', description: 'General-purpose web scraping runs' },
  'scraping.proxy': { title: 'Proxy Health', description: 'BrightData proxy pool status and IP rotation metrics' },
  'scraping.blocked': { title: 'Blocked Rate', description: 'Anti-bot detection analytics and mitigation' },
  'analytics.cost': { title: 'Cost Tracking', description: 'Per-agent and per-model cost attribution' },
  'settings.agents': { title: 'Agent Settings', description: 'Configure agent parameters and defaults' },
  'settings.connections': { title: 'Connections', description: 'Manage LinkedIn, Slack, and other integrations' },
  'settings.marketplace': { title: 'Marketplace', description: 'Browse and install skill packages' },
  'settings.apikeys': { title: 'API Keys', description: 'Manage tenant API keys and access tokens' },
}

interface DashboardProps {
  onSwitchMode: () => void
  /** Clears the stored token and returns to the login page. Omitted in stub mode. */
  onLogout?: () => void
}

export function Dashboard({ onSwitchMode, onLogout }: DashboardProps) {
  const [activePage, setActivePage] = useState('overview.home')
  // Two-level HR Job Postings state: null = show grid, non-null = show Kanban for that job
  const [selectedHRJob, setSelectedHRJob] = useState<ApiHRJobPosting | null>(null)
  // Two-level Sales deal drill (the HR pattern): null = list view, non-null =
  // the per-deal detail page, fetched fresh by id. Reset in navigate() rather
  // than an effect — ANY nav click (including re-clicking the same sales tab)
  // closes the detail and returns to the list.
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null)
  // Slice 3: Sales Home's primary action ("+ New Deal") lives in the shell's
  // slot (slice 2b (4)), so the drawer's OPEN state is held here and passed
  // down — the page keeps the drawer and its refresh, the bar keeps the button.
  const [homeNewDeal, setHomeNewDeal] = useState(false)
  // Slice 4: the Pipeline's New Deal drawer, opened from the same slot.
  const [pipelineNewDeal, setPipelineNewDeal] = useState(false)
  // Slice 5: the Contacts page's New Contact drawer, opened from the slot.
  const [contactsNew, setContactsNew] = useState(false)
  // Two-level Inbound Leads drill (§8.F), mirroring selectedDealId: null =
  // the queue, non-null = the per-lead detail. A convert clears it and sets
  // selectedDealId to the born deal — landing on Deal Detail (markBidWon's
  // behavioral precedent) with Back returning to the queue.
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)
  // Two-level Accounts drill (Slice A), mirroring selectedDealId: null = the
  // list, non-null = the account 360. A deal opened FROM the 360 stacks on
  // top (selectedDealId) and its Back returns to the account.
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)

  function navigate(page: string) {
    setSelectedDealId(null)
    setSelectedLeadId(null)
    setSelectedAccountId(null)
    setActivePage(page)
  }
  // Slice A: a Deal Detail's company link lands on that account's 360 —
  // from any sales tab — with the accounts tab active.
  function openAccount(accountId: string) {
    setSelectedDealId(null)
    setSelectedLeadId(null)
    setSelectedAccountId(accountId)
    setActivePage('sales.accounts')
  }
  // The session's server-side truth (GET /v1/me): resolved tenant + membership
  // role. null until loaded or on error — consumers fail closed (no tenant
  // shown, decide actions hidden).
  const [me, setMe] = useState<MeResponse | null>(null)

  useEffect(() => {
    fetchMe().then(setMe).catch(() => setMe(null))
  }, [])

  // Reset selected job whenever the user navigates away from the hr.job-postings view
  useEffect(() => {
    if (activePage !== 'hr.job-postings') {
      setSelectedHRJob(null)
    }
  }, [activePage])

  // Fail closed: no /me (or an empty role, the platform-operator case) means
  // no decide actions. The backend 403 remains the backstop.
  const canDecide = me?.role === 'owner' || me?.role === 'admin'

  // Same fail-closed pattern for feature entitlements: no /me → no features →
  // hidden. COSMETIC only — the withFeature 403 gate is the real boundary.
  const hasFeature = (key: string) => me?.features?.includes(key) ?? false

  function renderPage() {
    switch (activePage) {
      case 'overview.home':     return <OverviewHome onNavigate={navigate} canDecide={canDecide}
        // Deal Detail lives under the sales.dashboard case; setActivePage
        // directly — navigate() clears the selection it is meant to carry.
        onSelectDeal={(id) => { setActivePage('sales.dashboard'); setSelectedDealId(id) }} />
      case 'overview.health':   return <SystemHealth />
      // The sidebar's Operations group emits operations.* ids; map them to the
      // automation pages (the prototype's renderPage used the automation.* prefix).
      // M7b: operations.agents is the FIELD-TEAM page (the mockup's Agents
      // tab); the AI-agent AgentsList keeps the dormant automation.agents
      // alias — reachable from the Platform section behind
      // SHOW_PLATFORM_SECTION (unlinked, not gone).
      case 'automation.agents': return <AgentsList />
      case 'operations.agents': return <OperationsFieldTeam onNavigate={navigate} />
      case 'automation.runs':
      case 'operations.runs':   return <ActiveRuns />
      case 'automation.scheduled':
      case 'operations.scheduled': return <ScheduledJobs />
      case 'scraping.linkedin':
      case 'operations.linkedin': return <LinkedInScraping />
      // M6: the ops-workspace Overview is the field-ops KPI page. SystemHealth
      // itself is untouched — still reachable at overview.health behind
      // SHOW_PLATFORM_SECTION (unlinked, not gone).
      case 'operations.overview': return <OperationsOverview />
      case 'operations.tasks':  return <OperationsTasks />
      case 'operations.sops':   return <OperationsSops />
      case 'operations.incidents': return <OperationsIncidents />
      case 'operations.assets': return <OperationsAssets />
      case 'operations.quality': return <OperationsQuality />
      case 'analytics.performance': return <RunPerformance />
      case 'analytics.models':  return <ModelUsage />
      case 'analytics.feedback': return <FeedbackData />
      case 'sales.dashboard':
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to dashboard" onNavigate={navigate} />
          : <SalesDashboard onNavigate={navigate} onSelectDeal={setSelectedDealId} sessionUser={me?.displayName ?? me?.email ?? null}
              newDealOpen={homeNewDeal} onNewDealClose={() => setHomeNewDeal(false)} />
      case 'sales.pipeline':
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to pipeline" onNavigate={navigate} />
          : <SalesPipeline onSelectDeal={setSelectedDealId} newDealOpen={pipelineNewDeal} onNewDealClose={() => setPipelineNewDeal(false)} />
      case 'sales.leads':
        // Three states: born-deal detail (post-convert) → lead detail → queue.
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to inbound leads" onNavigate={navigate} />
          : selectedLeadId
            ? <SalesLeadDetail
                leadId={selectedLeadId}
                onBack={() => setSelectedLeadId(null)}
                onConverted={wire => { setSelectedLeadId(null); setSelectedDealId(wire.id) }}
                onOpenDeal={id => { setSelectedLeadId(null); setSelectedDealId(id) }}
              />
            : <SalesLeads onSelectLead={setSelectedLeadId} />
      case 'sales.contacts':   return <SalesContacts onOpenAccount={openAccount} newContactOpen={contactsNew} onNewContactClose={() => setContactsNew(false)} />
      case 'sales.accounts':
        // Three states (the Inbound Leads shape): a deal opened from the
        // 360 → the account 360 → the list.
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to account" onNavigate={navigate} />
          : selectedAccountId
            ? <SalesAccountDetail accountId={selectedAccountId} onBack={() => setSelectedAccountId(null)} onSelectDeal={setSelectedDealId} onNavigate={navigate} />
            : <SalesAccounts onSelectAccount={setSelectedAccountId} />
      case 'sales.reports':
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to reports" onNavigate={navigate} />
          : <SalesReports onSelectDeal={setSelectedDealId} />
      case 'sales.activities': return <SalesActivities />
      case 'sales.archive':
        return selectedDealId
          ? <SalesDealDetail dealId={selectedDealId} onOpenAccount={openAccount} onBack={() => setSelectedDealId(null)} backLabel="Back to archive" onNavigate={navigate} />
          : <SalesArchive onSelectDeal={setSelectedDealId} onNavigate={navigate} />
      case 'hr.overview':    return <HROverview onNavigate={navigate} />
      case 'hr.employees':   return <HREmployees />
      case 'hr.lifecycle':   return <HRLifecycle />
      case 'hr.job-postings':
        return selectedHRJob
          ? <HRCandidates selectedJob={selectedHRJob} onBack={() => setSelectedHRJob(null)} />
          : <HRJobPostings onSelectJob={(job) => setSelectedHRJob(job)} />
      case 'hr.onboarding':  return <HROnboarding />
      case 'hr.org-chart':   return <HROrgChart />
      case 'hr.tickets':     return <HRTickets />
      case 'recruitment.candidates': return <HRCandidates />
      case 'recruitment.providers': return <HRProviders />
      case 'inbox.triage':   return <TriageQueue />
      case 'payroll.pay-run': return <PayrollPayRun />
      case 'finance.overview': return <FinanceOverview onNavigate={navigate} />
      case 'finance.invoices': return <FinanceInvoices />
      case 'finance.issued-invoices': return <IssuedInvoices />
      case 'finance.approvals': return <FinanceApprovals canDecide={canDecide} />
      case 'finance.rejections': return <FinanceRejections />
      case 'finance.expenses': return <FinanceExpenses sessionUser={me?.displayName ?? me?.email ?? null} />
      case 'finance.reports': return <FinanceReports />
      case 'settings.company': return <SettingsPage me={me} />
      default: {
        // Remaining finance.*/hr.* ids without a page yet → honest "coming soon".
        // FIN-1: name the PAGE that isn't built, not the module — Finance
        // has seven live pages, so the shared default ("not yet connected")
        // was false here. Budgets is the only finance nav item without a case.
        if (activePage.startsWith('finance.')) return <ComingSoon module="Budgets"
          description="Budgets is the one Finance page still to be built. Everything else in Finance — invoices, expenses, approvals and reports — is live and reading real data." />
        if (activePage.startsWith('hr.')) return <ComingSoon module="HR" />
        if (activePage.startsWith('payroll.')) return <ComingSoon module="Payroll" />
        const ph = PLACEHOLDER_PAGES[activePage]
        return ph
          ? <PlaceholderPage title={ph.title} description={ph.description} />
          : <PlaceholderPage title="Page not found" />
      }
    }
  }

  return (
    <DashboardLayout
      activePage={activePage}
      onNavigate={navigate}
      onSwitchMode={onSwitchMode}
      onLogout={onLogout}
      tenantName={me?.tenantName ?? null}
      userDisplayName={me?.displayName ?? null}
      userEmail={me?.email ?? null}
      hasFeature={hasFeature}
      // Keyed to the RENDERED page, not the route id: sales.pipeline shows
      // the normally-scrolling Deal Detail while a deal is drilled (§6.5).
      viewportOwning={activePage === 'sales.pipeline' && !selectedDealId}
      // Slice 3: Sales Home's slot button and its own main padding — keyed to
      // the rendered page the same way (Deal Detail under this route is not Home).
      primaryAction={!selectedDealId && (activePage === 'sales.dashboard' || activePage === 'sales.pipeline')
        ? <button type="button" className="k-btn k-btn--lg inline-flex items-center gap-2"
            onClick={() => activePage === 'sales.dashboard' ? setHomeNewDeal(true) : setPipelineNewDeal(true)}><Plus size={14} /> New Deal</button>
        : activePage === 'sales.contacts'
          ? <button type="button" className="k-btn k-btn--lg inline-flex items-center gap-2" onClick={() => setContactsNew(true)}><Plus size={14} /> New Contact</button>
          : undefined}
      mainPadding={activePage === 'sales.dashboard' && !selectedDealId ? '36px 40px 32px' : activePage === 'sales.contacts' ? '28px 40px 24px' : undefined}
    >
      {renderPage()}
    </DashboardLayout>
  )
}
